/**
 * Builds a clean numeric axis domain + ticks from the plotted data max.
 * Prefer ~5 tick intervals with nice whole-number step sizes (1, 2, 5 × 10^n).
 * Never emits decimal ticks — referral counts are whole numbers only.
 */
export function buildNiceAxisScale(rawMax, {
  targetTicks = 5,
  headroom = 0.1,
} = {}) {
  const dataMax = Number.isFinite(rawMax) ? Math.max(0, Math.ceil(rawMax)) : 0;

  if (dataMax === 0) {
    return {
      domainMax: 5,
      ticks: [0, 1, 2, 3, 4, 5],
      step: 1,
    };
  }

  const paddedMax = Math.max(dataMax + 1, Math.ceil(dataMax * (1 + headroom)));
  const roughStep = Math.max(1, paddedMax / targetTicks);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const residual = roughStep / magnitude;

  let niceResidual;
  if (residual <= 1) niceResidual = 1;
  else if (residual <= 2) niceResidual = 2;
  else if (residual <= 5) niceResidual = 5;
  else niceResidual = 10;

  // Always use whole-number steps — never 0.5, 0.2, etc.
  const step = Math.max(1, niceResidual * magnitude);
  let domainMax = Math.ceil(paddedMax / step) * step;

  // Ensure the highest data point always has headroom above it.
  if (domainMax <= dataMax) {
    domainMax += step;
  }

  const ticks = [];
  for (let value = 0; value <= domainMax; value += step) {
    ticks.push(Math.round(value));
  }

  return {
    domainMax,
    ticks,
    step,
  };
}

export function getSeriesMax(rows, keys) {
  if (!rows?.length) return 0;
  return rows.reduce((max, row) => {
    const localMax = keys.reduce((inner, key) => {
      const value = Number(row[key]) || 0;
      return Math.max(inner, value);
    }, 0);
    return Math.max(max, localMax);
  }, 0);
}

/** Recharts measures the widest numeric tick instead of its default 60px axis. */
export const CLR_VALUE_Y_AXIS_WIDTH = 'auto';
